import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { TargetIcon, TrophyIcon, CheckCircleIcon, XCircleIcon, HourglassMediumIcon } from "@phosphor-icons/react";

function QuestionBlock({ gameId, q, onPicked }) {
  const [picking, setPicking] = useState(false);

  const pick = async (choice) => {
    if (q.pick || !q.open || picking) return;
    setPicking(true);
    try {
      await api.post(`/watch/${gameId}/predict`, { question_key: q.key, choice });
      onPicked();
    } catch (err) {
      captureError("predict failed:", err);
    } finally {
      setPicking(false);
    }
  };

  const labelFor = (val) => q.options.find((o) => o.value === val)?.label || val;

  let statusEl = null;
  if (q.pick && q.resolved) {
    statusEl = q.correct === true ? (
      <span className="inline-flex items-center gap-1 text-[#32D74B] text-[11px] font-black uppercase tracking-wider" data-testid={`predict-result-${q.key}`}>
        <CheckCircleIcon weight="fill" size={14} /> Correct · +{q.points} XP
      </span>
    ) : q.correct === false ? (
      <span className="inline-flex items-center gap-1 text-[#FF453A] text-[11px] font-black uppercase tracking-wider" data-testid={`predict-result-${q.key}`}>
        <XCircleIcon weight="fill" size={14} /> Missed
      </span>
    ) : (
      <span className="text-white/40 text-[11px] font-bold uppercase tracking-wider" data-testid={`predict-result-${q.key}`}>Push (no result)</span>
    );
  } else if (q.pick) {
    statusEl = (
      <span className="inline-flex items-center gap-1 text-[#0A84FF] text-[11px] font-bold uppercase tracking-wider" data-testid={`predict-pending-${q.key}`}>
        <HourglassMediumIcon size={14} /> Locked: {labelFor(q.pick)}
      </span>
    );
  }

  return (
    <div className="py-3 first:pt-0" data-testid={`predict-q-${q.key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-display font-bold text-sm uppercase tracking-tight">{q.prompt}</p>
        {statusEl}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {q.options.map((o) => {
          const isPick = q.pick === o.value;
          return (
            <button
              key={o.value}
              onClick={() => pick(o.value)}
              disabled={!!q.pick || !q.open || picking}
              data-testid={`predict-${q.key}-${o.value}`}
              className={`px-3 py-2.5 rounded-md border font-display font-bold text-sm uppercase tracking-tight transition-colors ${
                isPick ? "border-[#0A84FF] bg-[#0A84FF]/15 text-[#0A84FF]"
                  : q.pick || !q.open ? "border-white/10 text-white/40"
                  : "border-white/15 text-white/80 hover:border-[#0A84FF]/60 hover:bg-[#0A84FF]/5"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {!q.pick && q.open && <p className="text-[10px] text-white/35 mt-1.5 uppercase tracking-wider">+15 XP to predict</p>}
    </div>
  );
}

export default function PredictPanel({ gameId, gameStatus }) {
  const [questions, setQuestions] = useState([]);
  const [board, setBoard] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, lb] = await Promise.all([
        api.get(`/watch/${gameId}/predictions`),
        api.get("/predictions/leaderboard"),
      ]);
      setQuestions(p.data.questions || []);
      setBoard(lb.data || []);
    } catch (err) {
      captureError("predictions load failed:", err);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  // Re-resolve when game state changes (e.g. went FINAL)
  useEffect(() => {
    if (gameStatus === "FINAL") load();
  }, [gameStatus, load]);

  if (questions.length === 0) return null;

  return (
    <div className="mb-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 p-4 rounded-md bg-[#121212] border border-white/10" data-testid="predict-panel">
        <div className="flex items-center gap-2 mb-2">
          <TargetIcon weight="bold" size={18} className="text-[#0A84FF]" />
          <h3 className="font-display font-bold text-lg uppercase tracking-tighter">Predict the Next Play</h3>
        </div>
        <div className="divide-y divide-white/5">
          {questions.map((q) => <QuestionBlock key={q.key} gameId={gameId} q={q} onPicked={load} />)}
        </div>
      </div>

      <div className="p-4 rounded-md bg-[#121212] border border-white/10" data-testid="predict-leaderboard">
        <div className="flex items-center gap-2 mb-3">
          <TrophyIcon weight="fill" size={16} className="text-[#FDB927]" />
          <h3 className="font-display font-bold text-base uppercase tracking-tighter">Top Predictors</h3>
        </div>
        {board.length === 0 ? (
          <p className="text-xs text-white/40">No graded predictions yet. Make calls to climb the board.</p>
        ) : (
          <div className="space-y-1.5">
            {board.slice(0, 5).map((r) => (
              <div key={r.user_id} data-testid={`leader-${r.rank}`}
                className={`flex items-center justify-between px-2 py-1.5 rounded ${r.is_me ? "bg-[#0A84FF]/10 border border-[#0A84FF]/40" : ""}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-num text-xs font-black text-white/40 w-4">{r.rank}</span>
                  <span className="text-xs font-bold text-white/85 truncate">{r.name}{r.is_me ? " (You)" : ""}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-white/40">{r.accuracy}%</span>
                  <span className="font-num text-xs font-black text-[#FDB927]">{r.points}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
